import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { publicTrainingApi } from '../lib/api';
import { isValidCF } from '../lib/cf';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

export default function PublicRegisterPage() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    cognome: '',
    nome: '',
    codice_fiscale: '',
    luogo_nascita: '',
    data_nascita: '',
    azienda: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [cfError, setCfError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await publicTrainingApi.getSession(token);
        setSession(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Link non valido o scaduto');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleCfBlur = () => {
    const v = form.codice_fiscale.trim().toUpperCase();
    if (!v) { setCfError(''); return; }
    setCfError(isValidCF(v) ? '' : 'Codice Fiscale non valido');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cf = form.codice_fiscale.trim().toUpperCase();
    if (!form.cognome || !form.nome || !form.luogo_nascita || !form.data_nascita) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    if (!isValidCF(cf)) {
      setCfError('Codice Fiscale non valido');
      toast.error('Codice Fiscale non valido');
      return;
    }
    try {
      setSubmitting(true);
      await publicTrainingApi.enroll(token, { ...form, codice_fiscale: cf });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore iscrizione');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-slate-100 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-10">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Link non valido</h2>
            <p className="text-gray-600">{error || 'La sessione non è più disponibile'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!session.is_open) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-slate-100 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-10">
            <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Iscrizioni chiuse</h2>
            <p className="text-gray-600">
              Le iscrizioni a questa sessione formativa sono state chiuse.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-slate-100 p-6">
        <Card className="max-w-md w-full" data-testid="success-card">
          <CardContent className="text-center py-10">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Iscrizione completata!</h2>
            <p className="text-gray-600 mb-4">
              Grazie <strong>{form.nome} {form.cognome}</strong>, la tua iscrizione al corso è stata registrata correttamente.
            </p>
            <div className="bg-gray-50 p-4 rounded text-left text-sm">
              <p><strong>Corso:</strong> {session.title}</p>
              <p><strong>Data:</strong> {session.course_date_from}
                {session.course_date_to && session.course_date_to !== session.course_date_from
                  ? ` → ${session.course_date_to}` : ''}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-100 py-8 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm mb-4">
            <GraduationCap className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium">{session.ente_organizzatore || 'CEN Engineering'}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Registro Presenze</h1>
          <p className="text-gray-600 mt-1">Compila il modulo per iscriverti al corso</p>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
            <CardTitle className="text-xl">{session.title}</CardTitle>
            <div className="text-sm text-blue-100 mt-2 space-y-1">
              <div>📅 {session.course_date_from}
                {session.course_date_to && session.course_date_to !== session.course_date_from
                  ? ` → ${session.course_date_to}` : ''}
              </div>
              {session.sede && <div>📍 {session.sede}</div>}
              {session.total_hours && <div>⏱ {session.total_hours} ore</div>}
              {session.docente_nome && <div>👨‍🏫 Docente: {session.docente_nome}</div>}
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="enrollment-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Cognome *</Label>
                  <Input
                    required
                    value={form.cognome}
                    onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                    data-testid="input-cognome"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Nome *</Label>
                  <Input
                    required
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    data-testid="input-nome"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label>Codice Fiscale *</Label>
                <Input
                  required
                  maxLength={16}
                  className={`uppercase font-mono ${cfError ? 'border-red-500' : ''}`}
                  value={form.codice_fiscale}
                  onChange={(e) => {
                    setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() });
                    if (cfError) setCfError('');
                  }}
                  onBlur={handleCfBlur}
                  data-testid="input-cf"
                />
                {cfError && <p className="text-xs text-red-600">{cfError}</p>}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Luogo di nascita (Comune) *</Label>
                  <Input
                    required
                    value={form.luogo_nascita}
                    onChange={(e) => setForm({ ...form, luogo_nascita: e.target.value })}
                    data-testid="input-luogo"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Data di nascita *</Label>
                  <Input
                    required
                    type="date"
                    value={form.data_nascita}
                    onChange={(e) => setForm({ ...form, data_nascita: e.target.value })}
                    data-testid="input-data-nascita"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label>Azienda{!session.ragione_sociale ? '' : ' (se diversa da quella del corso)'}</Label>
                <Input
                  value={form.azienda}
                  onChange={(e) => setForm({ ...form, azienda: e.target.value })}
                  placeholder={session.ragione_sociale || 'Opzionale'}
                  data-testid="input-azienda"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="submit-enrollment-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Iscrizione in corso...
                  </>
                ) : (
                  'Conferma iscrizione'
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                I tuoi dati verranno utilizzati esclusivamente per la compilazione del registro presenze del corso.
              </p>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
